import React, { useEffect, useState, useMemo, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { currentWeek } from "@/lib/week";
import WeekPicker from "@/components/WeekPicker";
import { Check, Sparkles, Repeat, CalendarCheck } from "lucide-react";
import { LoadError, errMessage } from "@/components/LoadError";
import PrepReminder from "@/components/PrepReminder";
import { toast } from "sonner";
import { ToelichtingKnop, ToelichtingRegel } from "@/components/WeekritmeToelichting";
import { useWeekDagen, DagKop } from "@/components/WeekritmeDag";
import { ExtraGedaan, ExtraAndereDag, aantalExtra } from "@/components/WeekritmeExtra";

const DAY_ORDER = ["Maandag", "Dinsdag", "Woensdag", "Donderdag", "Vrijdag", "Zaterdag", "Zondag"];
const TYPE_BADGE = {
  discipline: { label: "Discipline", cls: "bg-amber-100 text-amber-800 border-amber-200", icon: Sparkles },
  rooster: { label: "Rooster", cls: "bg-blue-100 text-blue-800 border-blue-200", icon: Repeat },
};

// Eigen weekritme van de bedrijfsleider: eigen profiel-taken, zelf afgevinkt (accountability).
// Leest 'eigenWeekritmeTasks' uit /meta (NIET de OM-lijst) en schrijft alleen op de eigen username.
export default function MijnWeekritme() {
  const { user } = useAuth();
  const [week, setWeek] = useState(currentWeek());
  const [tasks, setTasks] = useState([]);
  const [profiel, setProfiel] = useState(null);
  const [metaLoaded, setMetaLoaded] = useState(false);
  const [checks, setChecks] = useState({});
  const [toel, setToel] = useState({});
  const [openToel, setOpenToel] = useState({});
  const [toelKey, setToelKey] = useState("");
  const toelSleutel = useRef("");
  toelSleutel.current = `${week}|${user.username}`;
  const [checksLoaded, setChecksLoaded] = useState(false);
  const [metaError, setMetaError] = useState("");
  const [checksError, setChecksError] = useState("");

  const loadMeta = () => api.get("/meta").then((r) => {
      setTasks(r.data.eigenWeekritmeTasks || []);
      setProfiel(r.data.profiel || null);
      setMetaLoaded(true);
      setMetaError("");
    }).catch((e) => setMetaError(errMessage(e)));
  useEffect(() => { loadMeta(); }, []);

  const checksSeq = useRef(0);
  const [extra, setExtra] = useState([]);   // v20 B: "Extra gedaan"
  const loadChecks = () => {
    const seq = ++checksSeq.current;
    setChecks({});
    setExtra([]);
    setToel({});
    setToelKey("");
    setOpenToel({});
    setChecksLoaded(false);
    api.get(`/weekritme?week=${week}&om=${user.username}`)
      .then((r) => { if (seq !== checksSeq.current) return; setChecks(r.data.checks || {}); setToel(r.data.toelichtingen || {}); setExtra(r.data.extra || []); setToelKey(`${week}|${user.username}`); setChecksLoaded(true); setChecksError(""); })
      .catch((e) => { if (seq === checksSeq.current) setChecksError(errMessage(e)); });
  };
  useEffect(loadChecks, [week]);

  const grouped = useMemo(() => {
    const g = {};
    for (const t of tasks) (g[t.day] = g[t.day] || []).push(t);
    return g;
  }, [tasks]);

  const editable = checksLoaded;
  const toelKlaar = checksLoaded && toelKey === `${week}|${user.username}`;
  const dagen = DAY_ORDER.filter((d) => grouped[d]);
  const wd = useWeekDagen({ week, sleutel: `${week}|${user.username}`, dagen, grouped, checks, klaar: checksLoaded && metaLoaded && !metaError });   // v18-R1: taken komen uit /meta
  const doneCount = tasks.filter((t) => checks[t.id]).length;
  const pct = Math.round((doneCount / (tasks.length || 1)) * 100);

  const toggle = async (taskId) => {
    if (!editable) return;
    const next = !checks[taskId];
    setChecks((c) => ({ ...c, [taskId]: next }));
    try {
      await api.post("/weekritme/toggle", { week, om: user.username, taskId, done: next });
    } catch (e) {
      setChecks((c) => ({ ...c, [taskId]: !next }));
      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
    }
  };

  return (
    <div data-testid="mijn-weekritme-page" className="pb-24">
      {user.role === "bedrijfsleider" && <PrepReminder />}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3 md:mb-4">
        <div>
          <h1 className="font-heading text-2xl font-black tracking-tight text-zinc-900">{user.role === "afdeling" ? "Weekritme" : "Mijn weekritme"}</h1>
          <p className="text-sm text-zinc-500">Jouw vaste ritme deze week — zelf afvinken. Reset per week.</p>
        </div>
        <WeekPicker week={week} setWeek={setWeek} />
      </div>

      {(metaError || checksError) && (
        <div className="mb-4 space-y-2">
          <LoadError error={metaError} onRetry={loadMeta} testid="mijn-weekritme-meta-error" />
          <LoadError error={checksError} onRetry={loadChecks} testid="mijn-weekritme-load-error" />
        </div>
      )}

      {metaLoaded && !metaError && !profiel && (
        <div data-testid="mijn-weekritme-no-profile" className="rounded-md bg-amber-50 border border-amber-200 px-5 py-4 text-sm text-amber-800">
          Er is nog geen weekritme-profiel aan je account toegewezen. Vraag je oversight dit in te stellen.
        </div>
      )}

      {metaLoaded && !metaError && profiel && tasks.length === 0 && (
        <div data-testid="mijn-weekritme-empty-profile" className="rounded-md bg-zinc-50 border border-zinc-200 px-5 py-4 text-sm text-zinc-500">
          Je weekritme-profiel is nog niet ingericht. Er staan nog geen taken klaar.
        </div>
      )}

      {profiel && (tasks.length > 0 || (toelKlaar && (editable || extra.length > 0))) && (
        <>
          <div className="flex items-center gap-4 mb-3 md:mb-4 rounded-[10px] bg-white border border-zinc-200 px-4 py-3">
            <div className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Voortgang</div>
            <div className="flex-1 h-2 bg-zinc-100 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500 transition-all duration-300" style={{ width: `${pct}%` }} />
            </div>
            <div data-testid="mijn-weekritme-progress" className="font-mono text-lg font-black text-zinc-900 tabular-nums">
              {doneCount}<span className="text-zinc-300">/{tasks.length}</span>
            </div>
          </div>

          {!editable && <p className="text-xs uppercase tracking-wider text-zinc-400 font-semibold mb-3">Laden…</p>}

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            {DAY_ORDER.filter((d) => grouped[d] || (toelKlaar && aantalExtra(extra, d) > 0)).map((day) => (
              <div key={day} className="bg-white rounded-[10px] border border-zinc-200 overflow-hidden hover:border-zinc-300 transition-colors">
                <DagKop dag={day} taken={grouped[day] || []} checks={checks} smal={wd.smal} open={wd.isOpen(day)} onToggle={() => wd.toggle(day)} voorbij={wd.voorbij(day)} prefix="mijn-weekritme" extra={toelKlaar ? aantalExtra(extra, day) : 0} />
                <div data-testid={`dag-taken-${day}`} className={`divide-y divide-zinc-200 ${wd.isOpen(day) ? "" : "hidden"}`}>
                  {(grouped[day] || []).map((t) => {
                    const badge = TYPE_BADGE[t.type];
                    const done = !!checks[t.id];
                    return (
                      <div key={t.id}>
                      <div className="flex items-start">
                      <button data-testid={`mijn-weekritme-task-${t.id}`} onClick={() => toggle(t.id)} disabled={!editable}
                        className={`flex-1 min-w-0 text-left flex items-start gap-3 px-4 py-3.5 transition-colors ${editable ? "hover:bg-zinc-50 cursor-pointer active:bg-zinc-100" : "cursor-default"}`}>
                        <span className={`mt-0.5 h-6 w-6 sm:h-5 sm:w-5 shrink-0 rounded-sm border flex items-center justify-center transition-colors ${done ? "bg-emerald-500 border-emerald-500" : "border-zinc-300 bg-white"}`}>
                          {done && <Check className="h-4 w-4 sm:h-3.5 sm:w-3.5 text-white" strokeWidth={3} />}
                        </span>
                        <span className="flex-1">
                          <span className={`text-sm ${done ? "text-zinc-400 line-through" : "text-zinc-700"}`}>{t.text}</span>
                          {badge && (
                            <span className={`ml-2 inline-flex items-center gap-1 align-middle text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${badge.cls}`}>
                              <badge.icon className="h-2.5 w-2.5" /> {badge.label}
                            </span>
                          )}
                        </span>
                      </button>
                      {editable && toelKlaar && <ToelichtingKnop taskId={t.id} open={!!openToel[t.id]} heeft={!!toel[t.id]} onClick={() => setOpenToel((o) => ({ ...o, [t.id]: !o[t.id] }))} />}
                      </div>
                      {toelKlaar && <ToelichtingRegel key={`${week}|${user.username}|${t.id}`} week={week} om={user.username} taskId={t.id} waarde={toel[t.id] || ""} editable={editable} open={!!openToel[t.id]}
                        onSaved={(id, v, w, o) => { if (`${w}|${o}` === toelSleutel.current) setToel((m) => ({ ...m, [id]: v })); }} />}
                      </div>
                    );
                  })}
                  {toelKlaar && <ExtraGedaan dag={day} week={week} items={extra} eigen={editable}
                    onAdd={(x) => { if (`${x.week}|${x.om}` === toelSleutel.current) setExtra((l) => [...l, x]); }}
                    onDelete={(id) => setExtra((l) => l.filter((y) => y.id !== id))} />}
                </div>
              </div>
            ))}
          </div>
          {toelKlaar && editable && <ExtraAndereDag week={week} onAdd={(x) => { if (`${x.week}|${x.om}` === toelSleutel.current) setExtra((l) => [...l, x]); }} />}
        </>
      )}
    </div>
  );
}
